import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Phone, Mail, User } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const LOGO_SVG = "https://customer-assets.emergentagent.com/job_115f2a70-e012-4ed0-b268-01edb5a7f1fc/artifacts/m7pu0oom_upscalemedia-transformed-2.svg";

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'Shop', to: '/shop' },
  { label: 'Corsi', to: '/corsi' },
  { label: 'Lezioni di prova GRATUITE', to: '/lezioni-prova', highlight: true },
  { label: 'Affitta Sala', to: '/affitta-sala' },
  { label: 'Contatti', to: '/contatti' },
];

export default function Navbar() {
  const { itemCount, setIsOpen } = useCart();
  const { user, login } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#0288D1] text-white text-sm py-1.5 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="tel:3801599564" className="flex items-center gap-1.5 hover:text-[#FFCA28] transition-colors">
              <Phone size={14} /> 380 159 9564
            </a>
            <a href="mailto:caribbeanbluemoon2025@gmail.com" className="flex items-center gap-1.5 hover:text-[#FFCA28] transition-colors">
              <Mail size={14} /> caribbeanbluemoon2025@gmail.com
            </a>
          </div>
          <Link to="/lezioni-prova" className="font-medium hover:text-[#FFCA28] transition-colors">
            Prenota una lezione gratuita
          </Link>
        </div>
      </div>

      {/* Main nav */}
      <nav
        data-testid="main-navbar"
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'glass shadow-lg shadow-slate-200/40'
            : 'bg-white/95 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
              <img src={LOGO_SVG} alt="Caribbean Blue Moon" className="h-10 md:h-14 w-auto" />
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`font-heading font-semibold text-sm tracking-wide uppercase transition-colors duration-200 ${
                    link.highlight
                      ? location.pathname === link.to
                        ? 'text-[#FF7043]'
                        : 'text-[#0288D1] hover:text-[#FF7043]'
                      : location.pathname === link.to
                      ? 'text-[#FF7043]'
                      : 'text-slate-700 hover:text-[#FF7043]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Link
                to="/lezioni-prova"
                data-testid="nav-prenota-btn"
                className="hidden lg:inline-flex items-center px-5 py-2.5 bg-[#FF7043] text-white font-heading font-bold text-sm rounded-full btn-caribbean"
              >
                Prenota Lezione
              </Link>
              {user ? (
                <Link to="/account" data-testid="nav-account-btn" className="p-1 rounded-full hover:ring-2 hover:ring-[#0288D1]/40 transition-all" title="Il mio account">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-[#0288D1] text-white flex items-center justify-center"><User size={16} /></span>
                  )}
                </Link>
              ) : (
                <button
                  data-testid="nav-login-btn"
                  onClick={login}
                  className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                  title="Accedi con Google"
                >
                  <User size={22} className="text-slate-700" />
                </button>
              )}
              <button
                data-testid="cart-toggle-btn"
                onClick={() => setIsOpen(true)}
                className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
              >
                <ShoppingBag size={22} className="text-slate-700" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#FF7043] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden p-2 rounded-full hover:bg-slate-100 transition-colors"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 pb-6 animate-in slide-in-from-top-2">
            <div className="flex flex-col gap-1 pt-4">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`px-4 py-3 rounded-xl font-heading font-semibold text-base transition-colors ${
                    location.pathname === link.to
                      ? 'bg-[#FFF8F5] text-[#FF7043]'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <Link to="/account" className="px-4 py-3 rounded-xl font-heading font-semibold text-base text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="mobile-nav-account">
                  <User size={18} /> Il mio Account
                </Link>
              ) : (
                <button onClick={login} className="px-4 py-3 rounded-xl font-heading font-semibold text-base text-slate-700 hover:bg-slate-50 flex items-center gap-2 text-left" data-testid="mobile-nav-login">
                  <User size={18} /> Accedi con Google
                </button>
              )}
              <Link
                to="/lezioni-prova"
                className="mt-2 text-center px-4 py-3 bg-[#FF7043] text-white font-heading font-bold rounded-full btn-caribbean"
              >
                Prenota Lezione Gratuita
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
